import { Buffer } from 'buffer';

import { saveAs } from 'file-saver';
import mime from 'mime';

import { validateAgainst } from '../validation/validateAgainst';
import {
  AttachmentFileContent,
  CreateDeleteAttachmentsDto,
  CreateUpdateAttachmentDto,
  CreateUpdateAttachmentDtoFromJSON,
  DefaultApi,
  GetAttachmentDto,
  GetLastEditorDto,
} from '../autogenerated/spdApiClient';
import { AttachmentSchema, AttachmentType } from '../validation/schemas';

export interface AttachmentContextInterface {
  getById: (id: string) => Promise<GetAttachmentDto>;
  getAllForExamined: (examinedId: string) => Promise<GetAttachmentDto[]>;
  getFile: (id: string) => Promise<AttachmentFileContent>;
  saveFileInBrowser: (
    id: string,
    name: string,
    method: 'download' | 'open' | string,
  ) => Promise<void>;
  create: (attachment: CreateUpdateAttachmentDto) => Promise<GetAttachmentDto>;
  update: (id: string, attachment: CreateUpdateAttachmentDto) => Promise<void>;
  createVoid: (attachment: CreateUpdateAttachmentDto) => Promise<void>;
  delete: (id: string) => Promise<void>;
  getLastEditor: (id: string) => Promise<GetLastEditorDto>;
  addDeleteAttachments: (
    attachments: CreateDeleteAttachmentsDto,
  ) => Promise<void>;
}

export type Attachment = GetAttachmentDto | CreateUpdateAttachmentDto;

export const newAttachment = () => {
  return CreateUpdateAttachmentDtoFromJSON({});
};

export const AttachmentContext = (api: DefaultApi) => {
  return {
    getById: (id: string) => api.attachmentControllerGet(id),
    getAllForExamined: (examinedId: string) =>
      api.attachmentControllerGetAllForExamined(examinedId),

    getFile: (id: string) => api.attachmentControllerGetFileDownload(id),
    create: (attachment: CreateUpdateAttachmentDto) =>
      api.attachmentControllerCreate(attachment),
    createVoid: async (attachment: CreateUpdateAttachmentDto) => {
      await api.attachmentControllerCreate(attachment);
    },
    update: (id: string, attachment: CreateUpdateAttachmentDto) =>
      api.attachmentControllerUpdate(id, attachment),
    delete: (id: string) => api.attachmentControllerDelete(id),
    saveFileInBrowser: (
      id: string,
      name: string,
      method: 'download' | 'open' | string,
    ) => {
      return api
        .attachmentControllerGetFileDownload(id)
        .then(fileContent => {
          const mimeType = mime.getType(name) || undefined;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const bufferData = (fileContent.buffer as any).data;
          const buffer = Buffer.from(bufferData);
          const blob = new Blob(
            [buffer],
            mimeType ? { type: mimeType } : undefined,
          );
          if (method === 'open') {
            window.open(URL.createObjectURL(blob));
          } else {
            saveAs(blob, name);
          }
        })
        .catch(error => {
          console.error('Error during saveFileInBrowser:', error);
          throw error;
        });
    },
    getLastEditor: (id: string) => api.attachmentControllerGetLastEditor(id),
    addDeleteAttachments: (attachments: CreateDeleteAttachmentsDto) =>
      api.attachmentControllerAddDeleteAttachments(attachments),
  };
};

export const validate = (attachment: CreateUpdateAttachmentDto) =>
  validateAgainst(AttachmentSchema, attachment);

export const attachmentData = (
  attach?: GetAttachmentDto,
  examinedId?: string,
): CreateUpdateAttachmentDto => {
  if (attach) {
    return CreateUpdateAttachmentDtoFromJSON({
      ...attach,
      ...{
        examinedId: attach.examined?.id,
        examId: attach.exam?.id,
        testId: attach.test?.id,
      },
    });
  } else {
    return CreateUpdateAttachmentDtoFromJSON({
      ...{ examinedId: examinedId, date: new Date() },
    });
  }
};

export const newAttachmentForObject = (type: AttachmentType, id?: string) => {
  const attachment = newAttachment();
  attachment.type = type;
  attachment.objectId = id as string;
  return attachment;
};
